import React, { useState, useEffect, useRef } from 'react';

import { Sword, Shield, Zap, Heart, Crown, Target, BookOpen, X, GripVertical } from 'lucide-react';

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();
  return (
    <footer className="w-full py-4 text-center">
      <p className="text-amber-300 text-sm">
        &copy; {currentYear} Czupik, Duggin, French, Ross.
      </p>
    </footer>
  );
};

// Trivia data and helper
const getRandomTrivia = (triviaQuestions) => {
  if (!triviaQuestions || triviaQuestions.length === 0) {
    return { question: 'Loading trivia...', options: ['A', 'B', 'C', 'D'], answer: 'A' };
  }
  return triviaQuestions[Math.floor(Math.random() * triviaQuestions.length)];
};

// Card database
const CARDS = {
  trojanHorse: {
    id: 'trojanHorse',
    name: 'The Trojan Horse',
    type: 'Event',
    cost: 2,
    rarity: 'legendary',
    normal: 'Deal 12 damage to all opponents after 2 turns',
    bonus: 'Deal 20 unavoidable damage to all opponents immediately',
    lore: 'The Greek deception that ended the war.',
    needsTarget: false
  },
  achillesStrike: {
    id: 'achillesStrike',
    name: "Achilles' Strike",
    type: 'Attack',
    cost: 1,
    rarity: 'common',
    normal: 'Deal 8 damage',
    bonus: 'Deal 14 damage + apply Bleed (2 per turn for 3 turns)',
    lore: "Achilles' unstoppable rage.",
    needsTarget: true
  },
  hectorDefense: {
    id: 'hectorDefense',
    name: "Hector's Defense",
    type: 'Defense',
    cost: 1,
    rarity: 'common',
    normal: 'Gain 10 Block (lasts one round)',
    bonus: 'Gain 10 Block (lasts one round) + Retaliate 7 damage when hit this turn',
    lore: 'The defender of Troy stands firm.',
    needsTarget: false
  },
  spearOfAchilles: {
    id: 'spearOfAchilles',
    name: 'Spear of Achilles',
    type: 'Artifact',
    cost: 2,
    rarity: 'rare',
    normal: 'Deal 14 damage',
    bonus: 'Deal 20 damage',
    lore: 'A spear forged for a demigod.',
    needsTarget: true
  },
  zeusThunderbolt: {
    id: 'zeusThunderbolt',
    name: "Zeus' Thunderbolt",
    type: 'God',
    cost: 2,
    rarity: 'rare',
    normal: 'Deal 12 damage to a random enemy',
    bonus: 'Deal 16 damage (you choose the target)',
    lore: 'The King of Gods strikes from Olympus.',
    needsTarget: 'bonus'
  },
  scaeanGate: {
    id: 'scaeanGate',
    name: 'Scaean Gate',
    type: 'Location',
    cost: 1,
    rarity: 'common',
    normal: 'Reduce next incoming attack by 50% (lasts one round)',
    bonus: 'Reduce next two attacks by 50% (lasts one round)',
    lore: 'The main gate of Troy stands strong.',
    needsTarget: false
  },
  // `prophecyOfThetis` card removed
  basicAttack: {
    id: 'basicAttack',
    name: 'Swift Strike',
    type: 'Attack',
    cost: 1,
    rarity: 'common',
    normal: 'Deal 5 damage',
    bonus: 'Deal 8 damage',
    lore: 'A basic but effective strike.',
    needsTarget: true
  },
  basicDefense: {
    id: 'basicDefense',
    name: 'Shield Wall',
    type: 'Defense',
    cost: 1,
    rarity: 'common',
    normal: 'Gain 7 Block (lasts one round)',
    bonus: 'Gain 12 Block (lasts one round)',
    lore: 'A solid defensive stance.',
    needsTarget: false
  },
  parisArrow: {
    id: 'parisArrow',
    name: "Paris' Arrow",
    type: 'Attack',
    cost: 1,
    rarity: 'uncommon',
    normal: 'Deal 7 damage',
    bonus: 'Deal 11 damage, ignore 50% of Block',
    lore: 'The arrow that felled Achilles.',
    needsTarget: true
  },

  ambrosia: {
    id: 'ambrosia',
    name: 'Ambrosia',
    type: 'Healing',
    cost: 1,
    rarity: 'uncommon',
    normal: 'Restore 8 Health',
    bonus: 'Restore 12 Health',
    lore: 'Food of the gods, offering modest vitality.',
    needsTarget: false
  },

  asclepiusRemedy: {
    id: 'asclepiusRemedy',
    name: "Asclepius' Remedy",
    type: 'Healing',
    cost: 1,
    rarity: 'common',
    normal: 'Restore 5 Health + Regen 1 for 2 turns',
    bonus: 'Restore 8 Health + Regen 2 for 2 turns',
    lore: 'A mild herbal remedy from the god of healing.',
    needsTarget: false
  }
};

// Hero definitions
const HERO_DESCRIPTIONS = {
  achilles: 'The greatest warrior of the Greek army.',
  hector: 'The mightiest warrior of the Trojan army.',
  odysseus: 'The cunning king of Ithaca.',
  paris: 'The Trojan prince who abducted Helen.',
  ajax: 'A Greek hero, second only to Achilles.',
  menelaus: 'King of Sparta, husband of Helen.',
  ares: 'The Greek god of war.'
};


const HEROES = {
  achilles: { id: 'achilles', name: 'Achilles', faction: 'Greek' },
  hector: { id: 'hector', name: 'Hector', faction: 'Trojan' },
  odysseus: { id: 'odysseus', name: 'Odysseus', faction: 'Greek' },
  paris: { id: 'paris', name: 'Paris', faction: 'Trojan' },
  ajax: { id: 'ajax', name: 'Ajax', faction: 'Greek' },
  menelaus: { id: 'menelaus', name: 'Menelaus', faction: 'Greek' },
  ares: { id: 'ares', name: 'Ares', faction: 'God' }
};

// Hero images. Uses SVG by default with PNG fallback. Assumes files are in `public/avatars/`.
const HERO_IMAGES = {
  achilles: '/avatars/achilles.svg',
  hector: '/avatars/hector.svg',
  odysseus: '/avatars/odysseus.svg',
  paris: '/avatars/paris.svg',
  ajax: '/avatars/ajax.svg',
  menelaus: '/avatars/menelaus.svg',
  ares: '/avatars/ares.svg'
};

const TrojanWarGame = () => {
  const [gameState, setGameState] = useState('heroSelect');
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialPage, setTutorialPage] = useState(0);
  const [players, setPlayers] = useState([]);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  // `selectedCard` removed (not used)
  const [triviaQuestion, setTriviaQuestion] = useState(null);
  const [gameLog, setGameLog] = useState([]);
  const [targetSelection, setTargetSelection] = useState(null);
  const [pendingCardExecution, setPendingCardExecution] = useState(null);
  const [currentTrivia, setCurrentTrivia] = useState(null);
  const [triviaResult, setTriviaResult] = useState(null);
  const [practiceStats, setPracticeStats] = useState({ correct: 0, total: 0 });
  const [triviaQuestions, setTriviaQuestions] = useState([]);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [roundCounter, setRoundCounter] = useState(0);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [drawPile, setDrawPile] = useState([]);
  const [discardPile, setDiscardPile] = useState([]);
  const [draggedPlayerIndex, setDraggedPlayerIndex] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  const [flippingCards, setFlippingCards] = useState([]); // entries like "playerIndex-handIndex"
  const flipTimeoutRef = useRef(null);

  // Credits page component
  const renderCreditsPage = () => {
    return (
      <div className="min-h-screen text-amber-100 p-8 flex flex-col" style={{backgroundColor: '#6b2f10'}}>
        <div className="max-w-4xl mx-auto flex-1 flex flex-col items-center justify-center">
          <div className="bg-stone-800 p-8 rounded-lg border-4 border-amber-600 w-full max-w-2xl">
            <h1 className="text-5xl font-bold text-center mb-8 text-amber-400" style={{fontFamily: 'serif'}}>
              CREDITS
            </h1>
            
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-amber-300 mb-4">About</h2>
              <p className="text-amber-200 text-lg leading-relaxed">
                Created for the Greek Mythology game project in Mr. Girard's English class.
              </p>
            </div>
            
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-amber-300 mb-4">Development Team</h2>
              <ul className="space-y-3 text-amber-200 text-xl">
                <li className="flex items-center">
                  <span className="mr-3">•</span>
                  <span>Andrew Czupik</span>
                </li>
                <li className="flex items-center">
                  <span className="mr-3">•</span>
                  <span>Will Duggin</span>
                </li>
                <li className="flex items-center">
                  <span className="mr-3">•</span>
                  <span>Jacob French</span>
                </li>
                <li className="flex items-center">
                  <span className="mr-3">•</span>
                  <span>Harrison Ross</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <button
                onClick={() => setGameState('heroSelect')}
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-lg text-xl font-bold transition-all hover:scale-105"
              >
                Back to Menu
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  };

  useEffect(() => {
    const loadTrivia = async () => {
      try {
        const response = await fetch('/trivia.json');
        const data = await response.json();
        setTriviaQuestions(data);
      } catch (error) {
        console.error('Failed to load trivia questions:', error);
      }
    };
    loadTrivia();
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setShowMobileWarning(true);
    }
  }, []);

  // Build an ordered opponent list starting from the next player
  const opponentsOrdered = [];
  for (let o = 1; o < players.length; o++) {
    const idx = (currentPlayerIndex + o) % players.length;
    if (idx !== currentPlayerIndex && players[idx]) opponentsOrdered.push({ player: players[idx], idx });
  }

  const addLog = (message, type = 'info') => {
    setGameLog(prev => {
      // Prevent duplicate messages
      if (prev.length > 0 && prev[0].message === message) {
        return prev;
      }
      return [{message, type, id: Date.now() + Math.random()}, ...prev.slice(0, 9)];
    });
  };

  const createHugeDeck = (multiplier = 5) => {
    const allCards = Object.keys(CARDS);
    const deck = [];
    for (let i = 0; i < multiplier; i++) {
        deck.push(...allCards);
    }
    return deck.sort(() => Math.random() - 0.5);
  };

  const initializePlayer = (hero, id) => {
    const heroData = HEROES[hero];
    return {
      id: id,
      hero: heroData.name,
      heroId: hero,
      faction: heroData.faction,
      hp: 100,
      maxHp: 100,
      block: 0,
      blockRoundApplied: -1,
      energy: 3,
      maxEnergy: 3,
      hand: [],
      effects: [],
      alive: true
    };
  };

  const startGame = () => {
    if (players.length < 4) {
      addLog('Need 4 players to start!', 'error');
      return;
    }

    const hugeDeck = createHugeDeck();
    let newDrawPile = [...hugeDeck];

    const initializedPlayers = players.map((p, idx) => {
      const player = initializePlayer(p.heroId, idx);
      
      // Draw initial hand
      for (let i = 0; i < 5; i++) {
        if (newDrawPile.length > 0) {
          player.hand.push(newDrawPile.pop());
        }
      }
      return player;
    });

    setDrawPile(newDrawPile);
    setDiscardPile([]);
    
    setPlayers(initializedPlayers);
    setGameState('playing');
    setCurrentPlayerIndex(0);
    setRoundCounter(0);

    // Flip all initial hand cards for player 0 once at game start
    if (initializedPlayers[0].hand.length > 0) {
      const initialKeys = initializedPlayers[0].hand.map((_, idx) => `0-${idx}`);
      setFlippingCards(initialKeys);

      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
      }
      flipTimeoutRef.current = setTimeout(() => {
        setFlippingCards([]);
        flipTimeoutRef.current = null;
      }, 550);
    }

    addLog(`${initializedPlayers[0].hero} begins the battle!`, 'turn');
  };

  const drawCards = (player, count, playerIndex, shouldFlip = true) => {
    const newPlayer = {...player};
    let newDrawPile = [...drawPile];
    let newDiscardPile = [...discardPile];
    const drawnThisCall = [];

    for (let i = 0; i < count; i++) {
        if (newPlayer.hand.length >= 6) {
            addLog('Hand is full! Cannot draw more cards.', 'info');
            break;
        }

        let cardToDraw = null;

        if (newDrawPile.length === 0) {
            if (newDiscardPile.length === 0) {
              addLog('No cards left to draw in any pile!', 'error'); // More specific message
              break;
            } else {
              // Discard pile has cards, shuffle into draw pile
              newDrawPile = [...newDiscardPile].sort(() => Math.random() - 0.5);
              newDiscardPile = [];
              addLog('Shuffling discard pile into draw pile...', 'info');
              // After shuffle, draw pile should have cards, so proceed to pop
              cardToDraw = newDrawPile.pop();
            }
        } else {
            // Draw pile has cards, just pop one
            cardToDraw = newDrawPile.pop();
        }

        if (cardToDraw) {
          const newIndex = newPlayer.hand.length; // index the card will occupy
          newPlayer.hand.push(cardToDraw);
          if (typeof playerIndex === 'number') {
            drawnThisCall.push(`${playerIndex}-${newIndex}`);
          }

        } else {
          // This case should ideally not be hit if logic is correct and piles are not empty
          // but as a failsafe, log if a card couldn't be drawn even if expected
          addLog('Failed to retrieve a card for drawing, despite piles not being empty. This should not happen.', 'error');
          break; // Stop trying to draw if somehow no card was retrieved
        }
    }

    // Trigger flip animation only on cards drawn in this call (if enabled)
    if (shouldFlip && drawnThisCall.length > 0) {
      setFlippingCards(drawnThisCall);

      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
      }
      flipTimeoutRef.current = setTimeout(() => {
        setFlippingCards([]);
        flipTimeoutRef.current = null;
      }, 550);
    }

    setDrawPile(newDrawPile);
    setDiscardPile(newDiscardPile);
    return newPlayer;
  };

  const playCard = (cardId) => {
    const player = players[currentPlayerIndex];
    const card = CARDS[cardId];
    
    if (player.energy < card.cost) {
      addLog('Not enough energy!', 'error');
      return;
    }
    // ALWAYS show trivia first, before target selection
    // Show trivia question immediately
    showTriviaQuestion(cardId, null);
  };

  const selectTarget = (targetId) => {
    if (!pendingCardExecution) return;
    
    // Execute card with the selected target
    executeCardEffect(pendingCardExecution.cardId, targetId, pendingCardExecution.bonusActive);
    setTargetSelection(null);
    setPendingCardExecution(null);
  };

  const showTriviaQuestion = (cardId, targetId) => {
    const randomTrivia = getRandomTrivia(triviaQuestions);
    setTriviaQuestion({
      card: cardId,
      question: randomTrivia.question,
      options: randomTrivia.options,
      answer: randomTrivia.answer,
      targetId: targetId
    });
  };

  const answerTrivia = (answer) => {
    const isCorrect = answer === triviaQuestion.answer;
    
    // Show result screen
    setTriviaResult({
      correct: isCorrect,
      card: triviaQuestion.card
    });
  };

  const continueAfterTrivia = () => {
    if (!triviaResult) return;
    
    const card = CARDS[triviaResult.card];
    const isCorrect = triviaResult.correct;
    
    addLog(`${players[currentPlayerIndex].hero} plays ${card.name}`, 'card');
    
    if (isCorrect) {
      addLog(`✓ Trivia CORRECT! Bonus effect activated!`, 'success');
    } else {
      addLog(`✗ Trivia incorrect. Normal effect only.`, 'warning');
    }
    
    // Check if card needs target selection
    const needsTarget = card.needsTarget === true || (card.needsTarget === 'bonus' && isCorrect);
    
    if (needsTarget) {
      setPendingCardExecution({
        cardId: triviaResult.card,
        bonusActive: isCorrect
      });
      setTargetSelection('selectTarget');
      setTriviaQuestion(null);
      setTriviaResult(null);
    } else {
      // Execute card without target
      executeCardEffect(triviaResult.card, null, isCorrect);
      setTriviaQuestion(null);
      setTriviaResult(null);
      setPendingCardExecution(null);
    }
  };

  const executeCardEffect = (cardId, targetId, bonus) => {
    const card = CARDS[cardId];
    const newPlayers = [...players];
    const currentPlayer = newPlayers[currentPlayerIndex];
    
    // Remove card from hand and pay cost
    const cardIndex = currentPlayer.hand.indexOf(cardId);
    if (cardIndex > -1) {
      currentPlayer.hand.splice(cardIndex, 1);
    }
    setDiscardPile(prev => [...prev, cardId]);
    currentPlayer.energy -= card.cost;
    
    // Apply card effects
    switch (card.id) {
      case 'achillesStrike':
        const damage1 = bonus ? 14 : 8;
        addLog(`Dealing ${damage1} damage...`, 'info');
        dealDamage(newPlayers, currentPlayerIndex, targetId, damage1);
        if (bonus) {
          newPlayers[targetId].effects.push({type: 'bleed', value: 2, turns: 3});
          addLog(`${newPlayers[targetId].hero} is bleeding (2 dmg/turn)!`, 'effect');
        }
        break;
        
      case 'hectorDefense':
        currentPlayer.block += 10;
        currentPlayer.blockRoundApplied = roundCounter;
        addLog(`${currentPlayer.hero} gains 10 block`, 'effect');
        if (bonus) {
          currentPlayer.effects.push({type: 'retaliate', value: 7, turns: 1});
          addLog(`${currentPlayer.hero} will retaliate for 7 damage!`, 'effect');
        }
        break;
        
      case 'spearOfAchilles':
        const damage2 = bonus ? 20 : 14;
        addLog(`Dealing ${damage2} damage...`, 'info');
        dealDamage(newPlayers, currentPlayerIndex, targetId, damage2);
        break;
        
      case 'zeusThunderbolt':
        const zDamage = bonus ? 16 : 12;
        if (bonus && targetId !== null) {
          addLog(`Zeus targets ${newPlayers[targetId].hero}!`, 'info');
          dealDamage(newPlayers, currentPlayerIndex, targetId, zDamage);
        } else {
          const aliveOpponents = newPlayers.filter((p, i) => i !== currentPlayerIndex && p.alive);
          if (aliveOpponents.length > 0) {
            const aliveIndices = newPlayers.map((p, i) => i).filter(i => i !== currentPlayerIndex && newPlayers[i].alive);
            const randomIndex = aliveIndices[Math.floor(Math.random() * aliveIndices.length)];
            addLog(`Zeus strikes ${newPlayers[randomIndex].hero} randomly!`, 'info');
            dealDamage(newPlayers, currentPlayerIndex, randomIndex, zDamage);
          }
        }
        break;
        
      case 'scaeanGate':
        const count = bonus ? 2 : 1;
        currentPlayer.effects.push({type: 'damageReduction', value: 50, count: count, turns: 2});
        addLog(`${currentPlayer.hero} gains 50% damage reduction for the next ${count} attack(s)!`, 'effect');
        break;
        
      case 'trojanHorse':
        if (bonus) {
          addLog(`The Trojan Horse strikes immediately!`, 'info');
          newPlayers.forEach((p, i) => { // Effects might impact other players in the newPlayers array
            if (i !== currentPlayerIndex && p.alive) {
              dealDamage(newPlayers, currentPlayerIndex, i, 20, true);
            }
          });
        } else {
          currentPlayer.effects.push({type: 'delayedAoe', value: 12, turns: 2});
          addLog(`The Trojan Horse is being prepared (2 turns)...`, 'effect');
        }
        break;
        
      // Prophecy card removed; related effects handled by trivia/default mechanics
        
      case 'basicAttack':
        const damage3 = bonus ? 8 : 5;
        addLog(`Dealing ${damage3} damage...`, 'info');
        dealDamage(newPlayers, currentPlayerIndex, targetId, damage3);
        break;
        
      case 'basicDefense':
        const blockGain = bonus ? 12 : 7;
        currentPlayer.block += blockGain;
        currentPlayer.blockRoundApplied = roundCounter;
        addLog(`${currentPlayer.hero} gains ${blockGain} block`, 'effect');
        break;
        
      case 'parisArrow':
        const arrowDamage = bonus ? 11 : 7;
        const blockIgnorePercent = bonus ? 0.5 : 0;
        if (bonus) {
          addLog(`Paris' arrow ignores 50% of block!`, 'info');
        }
        addLog(`Dealing ${arrowDamage} damage...`, 'info');
        dealDamage(newPlayers, currentPlayerIndex, targetId, arrowDamage, false, blockIgnorePercent);
        break;
      case 'ambrosia':
        const heal1 = bonus ? 12 : 8;
        currentPlayer.hp = Math.min(currentPlayer.maxHp, currentPlayer.hp + heal1);
        addLog(`${currentPlayer.hero} restores ${heal1} health!`, 'success');
        break;

      case 'asclepiusRemedy':
        const heal3 = bonus ? 8 : 5;
        const regenValue = bonus ? 2 : 1;
        currentPlayer.hp = Math.min(currentPlayer.maxHp, currentPlayer.hp + heal3);
        addLog(`${currentPlayer.hero} restores ${heal3} health!`, 'success');
        currentPlayer.effects.push({type: 'regen', value: regenValue, turns: 2});
        addLog(`${currentPlayer.hero} will regenerate ${regenValue} health for 2 turns!`, 'effect');
        break;
      default:
        addLog(`No effect implemented for card: ${card.id}`, 'info');
        break;
    }
    
    setPlayers(newPlayers);
  };

  const dealDamage = (playersList, attackerId, targetId, baseDamage, unavoidable = false, blockIgnore = 0) => {
    if (targetId === null || targetId === undefined) return;
    
    const target = playersList[targetId];
    const attacker = playersList[attackerId];
    
    if (!target.alive) return;
    
    let damage = baseDamage;
    
    // Apply damage reduction effects
    const reductionEffectIndex = target.effects.findIndex(e => e.type === 'damageReduction');
    if (reductionEffectIndex !== -1 && !unavoidable) {
      const reductionEffect = target.effects[reductionEffectIndex];
      damage = Math.ceil(damage * (1 - reductionEffect.value / 100));
      addLog(`${target.hero}'s Scaean Gate reduces damage!`, 'effect');
      reductionEffect.count--;
      if (reductionEffect.count === 0) {
        target.effects.splice(reductionEffectIndex, 1);
      }
    }
    
    // Apply block
    if (!unavoidable) {
      const effectiveBlock = Math.floor(target.block * (1 - blockIgnore));
      const blockedDamage = Math.min(effectiveBlock, damage);
      target.block -= blockedDamage;
      damage -= blockedDamage;
    }
    
    // Deal damage
    target.hp = Math.max(0, target.hp - damage);
    
    addLog(`${attacker.hero} deals ${damage} damage to ${target.hero}!`, 'damage');
    
    // Check retaliate
    const retaliateEffect = target.effects.find(e => e.type === 'retaliate');
    if (retaliateEffect && attacker.alive) {
      const retDamage = Math.max(0, retaliateEffect.value - Math.max(0, attacker.block));
      attacker.block = Math.max(0, attacker.block - retaliateEffect.value);
      attacker.hp = Math.max(0, attacker.hp - retDamage);
      if (retDamage > 0) {
        addLog(`${target.hero} retaliates for ${retDamage} damage!`, 'damage');
      }
    }
    
    // Check death
    if (target.hp <= 0) {
      target.alive = false;
      addLog(`💀 ${target.hero} has been defeated!`, 'death');
      
      // Check win condition
      const alivePlayers = playersList.filter(p => p.alive);
      if (alivePlayers.length === 1) {
        setGameState('gameOver');
        addLog(`🏆 ${alivePlayers[0].hero} wins the Trojan War!`, 'victory');
      }
    }
  };

  const endTurn = () => {
    // First, flip the current player's hand to show card backs before changing turns
    const current = players[currentPlayerIndex];
    const outgoingKeys = current.hand.map((_, idx) => `${currentPlayerIndex}-${idx}`);

    if (outgoingKeys.length > 0) {
      setFlippingCards(outgoingKeys);

      if (flipTimeoutRef.current) {
        clearTimeout(flipTimeoutRef.current);
      }

      flipTimeoutRef.current = setTimeout(() => {
        setFlippingCards([]);
        flipTimeoutRef.current = null;

        // After the flip completes, process the actual end-of-turn logic
        const newPlayers = [...players]; 
        const playerToUpdateIndex = currentPlayerIndex; // The index of the player whose turn is ending
        const playerToUpdate = { ...newPlayers[playerToUpdateIndex] }; 
        
        // Apply effects to the playerToUpdate before their hand is discarded
        playerToUpdate.effects = playerToUpdate.effects.filter(effect => {
          if (effect.type === 'bleed') {
            playerToUpdate.hp = Math.max(0, playerToUpdate.hp - effect.value);
            addLog(`${playerToUpdate.hero} takes ${effect.value} bleed damage`, 'damage');
            effect.turns--;
            return effect.turns > 0;
          }
          if (effect.type === 'regen') {
            playerToUpdate.hp = Math.min(playerToUpdate.maxHp, playerToUpdate.hp + effect.value);
            addLog(`${playerToUpdate.hero} regenerates ${effect.value} health`, 'success');
            effect.turns--;
            return effect.turns > 0;
          }
          if (effect.type === 'delayedAoe') {
            effect.turns--;
            if (effect.turns === 0) {
              newPlayers.forEach((p, i) => { // Effects might impact other players in the newPlayers array
                if (i !== playerToUpdateIndex && p.alive) {
                  dealDamage(newPlayers, playerToUpdateIndex, i, effect.value, true);
                }
              });
              addLog(`💥 The Trojan Horse strikes!`, 'damage');
            }
            return effect.turns > 0;
          }
          if (effect.type === 'damageReduction' || effect.type === 'retaliate') {
            effect.turns--;
            return effect.turns > 0;
          }
          return true;
        });

        // Capture cards from the player's hand before emptying it
        const cardsToDiscard = [...playerToUpdate.hand]; 
        playerToUpdate.hand = []; // Empty the hand of the player copy

        // Update the player in the newPlayers array with the modified playerToUpdate
        newPlayers[playerToUpdateIndex] = playerToUpdate;

        // Determine next player
        let nextIndex = (playerToUpdateIndex + 1) % newPlayers.length;
        while (!newPlayers[nextIndex].alive && newPlayers.filter(p => p.alive).length > 1) {
          nextIndex = (nextIndex + 1) % newPlayers.length;
        }
        
        // If we've cycled back to player 0, increment round counter
        let newRound = roundCounter;
        if (nextIndex === 0) {
          newRound = roundCounter + 1;
        }
        
        // Reset block for all players whose block was applied in a previous round
        newPlayers.forEach((p) => {
          if (p.blockRoundApplied !== -1 && p.blockRoundApplied < newRound) {
            p.block = 0;
            p.blockRoundApplied = -1;
          }
        });
        
        // Start next turn: current player's hand is empty, next player draws (no flip yet)
        newPlayers[nextIndex].energy = newPlayers[nextIndex].maxEnergy;
        const updatedNextPlayer = drawCards(newPlayers[nextIndex], 5, nextIndex, false);

        newPlayers[nextIndex] = updatedNextPlayer;

        // Now set the state variables
        setPlayers(newPlayers); // Update players array first with all modifications
        setDiscardPile(prev => [...prev, ...cardsToDiscard]); // Then update discard pile with cards from previous turn
        setCurrentPlayerIndex(nextIndex);
        setRoundCounter(newRound);
        addLog(`⚔️ ${newPlayers[nextIndex].hero}'s turn begins`, 'turn');

        // After state update, flip the newly drawn next player's hand to reveal it
        const revealKeys = updatedNextPlayer.hand.map((_, idx) => `${nextIndex}-${idx}`);
        if (revealKeys.length > 0) {
          setFlippingCards(revealKeys);

          if (flipTimeoutRef.current) {
            clearTimeout(flipTimeoutRef.current);
          }

          flipTimeoutRef.current = setTimeout(() => {
            setFlippingCards([]);
            flipTimeoutRef.current = null;
          }, 550);
        }
      }, 550);
    } else {
      // If no cards in hand, just process end-of-turn logic immediately
      const newPlayers = [...players]; 
      const playerToUpdateIndex = currentPlayerIndex; // The index of the player whose turn is ending
      const playerToUpdate = { ...newPlayers[playerToUpdateIndex] }; 
      
      playerToUpdate.effects = playerToUpdate.effects.filter(effect => {
        if (effect.type === 'bleed') {
          playerToUpdate.hp = Math.max(0, playerToUpdate.hp - effect.value);
          addLog(`${playerToUpdate.hero} takes ${effect.value} bleed damage`, 'damage');
          effect.turns--;
          return effect.turns > 0;
        }
        if (effect.type === 'regen') {
          playerToUpdate.hp = Math.min(playerToUpdate.maxHp, playerToUpdate.hp + effect.value);
          addLog(`${playerToUpdate.hero} regenerates ${effect.value} health`, 'success');
          effect.turns--;
          return effect.turns > 0;
        }
        if (effect.type === 'delayedAoe') {
          effect.turns--;
          if (effect.turns === 0) {
            newPlayers.forEach((p, i) => {
              if (i !== playerToUpdateIndex && p.alive) {
                dealDamage(newPlayers, playerToUpdateIndex, i, effect.value, true);
              }
            });
            addLog(`💥 The Trojan Horse strikes!`, 'damage');
          }
          return effect.turns > 0;
        }
        if (effect.type === 'damageReduction' || effect.type === 'retaliate') {
          effect.turns--;
          return effect.turns > 0;
        }
        return true;
      });

      const cardsToDiscard = [...playerToUpdate.hand]; 
      playerToUpdate.hand = [];
      newPlayers[playerToUpdateIndex] = playerToUpdate;

      let nextIndex = (playerToUpdateIndex + 1) % newPlayers.length;
      while (!newPlayers[nextIndex].alive && newPlayers.filter(p => p.alive).length > 1) {
        nextIndex = (nextIndex + 1) % newPlayers.length;
      }

      let newRound = roundCounter;
      if (nextIndex === 0) {
        newRound = roundCounter + 1;
      }

      newPlayers.forEach((p) => {
        if (p.blockRoundApplied !== -1 && p.blockRoundApplied < newRound) {
          p.block = 0;
          p.blockRoundApplied = -1;
        }
      });

      newPlayers[nextIndex].energy = newPlayers[nextIndex].maxEnergy;
      const updatedNextPlayer = drawCards(newPlayers[nextIndex], 5, nextIndex, false);

      newPlayers[nextIndex] = updatedNextPlayer;

      setPlayers(newPlayers);
      setDiscardPile(prev => [...prev, ...cardsToDiscard]);
      setCurrentPlayerIndex(nextIndex);
      setRoundCounter(newRound);
      addLog(`⚔️ ${newPlayers[nextIndex].hero}'s turn begins`, 'turn');

      // Flip the newly drawn next player's hand to reveal it
      const revealKeys = updatedNextPlayer.hand.map((_, idx) => `${nextIndex}-${idx}`);
      if (revealKeys.length > 0) {
        setFlippingCards(revealKeys);

        if (flipTimeoutRef.current) {
          clearTimeout(flipTimeoutRef.current);
        }

        flipTimeoutRef.current = setTimeout(() => {
          setFlippingCards([]);
          flipTimeoutRef.current = null;
        }, 550);
      }
    }
  };

  const handleDrawAndDiscard = () => {
    const player = players[currentPlayerIndex];
    if (player.energy < 1) {
      addLog('Not enough energy to draw a card!', 'error');
      return;
    }

    const newPlayers = [...players];
    const currentPlayer = newPlayers[currentPlayerIndex];

    // Pay energy first, as drawing or discarding for a draw costs 1 energy
    currentPlayer.energy -= 1;
    setPlayers(newPlayers);

    // Check hand size
    if (currentPlayer.hand.length < 6) {
      // Draw a card directly if hand is not full
      const updatedPlayer = drawCards(currentPlayer, 1, currentPlayerIndex);
      newPlayers[currentPlayerIndex] = updatedPlayer;
      setPlayers(newPlayers);
      addLog('You drew a card.', 'info');
    } else {
      // If hand is full, enter discarding mode
      setIsDiscarding(true);
      addLog('Hand is full. Discard a card to draw a new one.', 'info');
    }
  };

  const handleDiscard = (cardId) => {
      const newPlayers = [...players];
      const currentPlayer = newPlayers[currentPlayerIndex];

      // Find the specific instance of the card to discard
      const cardIndex = currentPlayer.hand.indexOf(cardId);
      if (cardIndex > -1) {
          const [discardedCard] = currentPlayer.hand.splice(cardIndex, 1);
          setDiscardPile(prev => [...prev, discardedCard]);
          addLog(`You discarded ${CARDS[discardedCard].name}.`, 'info');

          // Draw a card
          const updatedPlayer = drawCards(currentPlayer, 1, currentPlayerIndex);
          newPlayers[currentPlayerIndex] = updatedPlayer;
          setPlayers(newPlayers);
          addLog('You drew a new card.', 'info');
      }

      setIsDiscarding(false);
  };

  const handleShuffleDiscard = () => {
    const player = players[currentPlayerIndex];
    if (player.energy < 2) {
      addLog('Not enough energy to shuffle discard!', 'error');
      return;
    }
    if (discardPile.length === 0) {
      addLog('Discard pile is empty!', 'warning');
      return;
    }

    const newPlayers = [...players];
    const currentPlayer = newPlayers[currentPlayerIndex];

    // Pay energy
    currentPlayer.energy -= 2;
    setPlayers(newPlayers);

    // Shuffle discard into draw pile
    const newDrawPile = [...discardPile].sort(() => Math.random() - 0.5);
    setDrawPile(prev => [...prev, ...newDrawPile]);
    setDiscardPile([]);
    addLog('Discard pile shuffled into draw pile!', 'info');
  };

  const selectHero = (heroId) => {
    const existingIndex = players.findIndex(p => p.heroId === heroId);
    if (existingIndex !== -1) {
      // already selected -> remove
      setPlayers(players.filter((_, i) => i !== existingIndex));
      return;
    }
    // add if space
    if (players.length < 4) {
      setPlayers([...players, {id: players.length, heroId}]);
    }
  };

  const removePlayer = (index) => {
    setPlayers(players.filter((_, i) => i !== index));
  };

  const handleDragStart = (index) => {
    setDraggedPlayerIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDrop = (targetIndex) => {
    if (draggedPlayerIndex === null || draggedPlayerIndex === undefined) {
      return;
    }

    // targetIndex is where we want to insert (0 = before first, 1 = before second, etc.)
    // If dragging backward and dropping right after itself, no change
    if (draggedPlayerIndex < targetIndex && draggedPlayerIndex === targetIndex - 1) {
      setDraggedPlayerIndex(null);
      return;
    }
    
    // If dragging forward and dropping at same position, no change
    if (draggedPlayerIndex >= targetIndex && draggedPlayerIndex === targetIndex) {
      setDraggedPlayerIndex(null);
      return;
    }

    const newPlayers = [...players];
    const draggedItem = newPlayers[draggedPlayerIndex];
    
    // Remove the dragged item from its original position
    newPlayers.splice(draggedPlayerIndex, 1);
    
    // Calculate the correct insert index after removal
    // targetIndex is where we want to insert
    let insertIndex = targetIndex;
    
    // If we removed an item before the target position, adjust the index
    if (draggedPlayerIndex < targetIndex) {
      insertIndex = targetIndex - 1;
    }
    
    // Ensure insertIndex is valid (can be equal to array length for inserting at the end)
    insertIndex = Math.max(0, Math.min(insertIndex, newPlayers.length));
    
    newPlayers.splice(insertIndex, 0, draggedItem);
    
    setPlayers(newPlayers);
    setDraggedPlayerIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedPlayerIndex(null);
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'legendary': return 'border-yellow-500';
      case 'rare': return 'border-purple-600';
      case 'uncommon': return 'border-green-600';
      default: return 'border-gray-600';
    }
  };

  const getRarityHover = (rarity) => {
    switch (rarity) {
      case 'legendary': return 'ring-4 ring-yellow-400/25';
      case 'rare': return 'ring-4 ring-purple-500/25';
      case 'uncommon': return 'ring-4 ring-green-400/25';
      default: return 'ring-4 ring-stone-400/10';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Attack': return <Sword className="w-4 h-4" />;
      case 'Defense': return <Shield className="w-4 h-4" />;
      case 'God': return <Zap className="w-4 h-4" />;
      case 'Event': return <Crown className="w-4 h-4" />;
      case 'Healing': return <Heart className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  const renderEffects = (effects) => {
    if (!effects || effects.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {effects.map((effect, index) => {
          let Icon, color, tooltip, displayText;
          switch (effect.type) {
            case 'regen':
              Icon = Heart;
              color = 'text-green-500';
              displayText = `+${effect.value} x${effect.turns}`;
              tooltip = `Regenerates ${effect.value} HP per turn for ${effect.turns} turn(s)`;
              break;
            case 'damageReduction':
              Icon = Shield;
              color = 'text-purple-500';
              displayText = `${effect.value}% x${effect.count}`;
              tooltip = `Reduces incoming damage by ${effect.value}% for next ${effect.count} attack(s)`;
              break;
            case 'bleed':
              Icon = Sword;
              color = 'text-red-500';
              displayText = `-${effect.value} x${effect.turns}`;
              tooltip = `Takes ${effect.value} damage per turn for ${effect.turns} turn(s)`;
              break;
            case 'retaliate':
              Icon = Sword;
              color = 'text-yellow-500';
              displayText = `${effect.value} x${effect.turns}`;
              tooltip = `Retaliates ${effect.value} damage when hit for ${effect.turns} turn(s)`;
              break;
            case 'delayedAoe':
              Icon = Target;
              color = 'text-orange-500';
              displayText = `${effect.value} in ${effect.turns}`;
              tooltip = `Delayed AoE: Deals ${effect.value} damage to all opponents in ${effect.turns} turn(s)`;
              break;
            default:
              return null;
          }
          return (
            <div key={index} className="group relative">
              <div className={`flex items-center gap-1 ${color}`}>
                <Icon className="w-4 h-4" />
                <span className="text-sm">{displayText}</span>
              </div>
              <div className="absolute z-10 hidden group-hover:block bg-stone-800 p-2 rounded border border-amber-600 text-sm text-amber-100 whitespace-nowrap -top-full left-0 transform -translate-y-2">
                {tooltip}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const tutorialPages = [
    {
      title: "Welcome to the Trojan War!",
      content: "This is a hotseat, turn-based card battle game where 4 heroes share one device and take turns. On your turn, you play cards from your hand to attack, defend, or heal. The last hero standing wins."
    },
    {
      title: "Energy System",
      content: "You have 3 energy per turn. Each card costs energy to play (shown in the blue circle). You can play multiple cards on your turn as long as you have enough energy. When you're done playing cards, click END TURN to pass to the next hero."
    },
    {
      title: "Trivia Bonus Effects",
      content: "Every card has TWO effects:\n\n• NORMAL effect (always happens)\n• BONUS effect (only if you answer trivia correctly)\n\nAnswer correctly to get stronger effects!"
    },
    {
      title: "Targeting",
      content: "Many Attack and God cards need a target. After you play one of these cards and answer the trivia, click on the opponent's hero portrait you want to hit. Red highlights will show you who can be targeted."
    },
    {
      title: "Healing & Defense Cards",
      content: "Healing cards restore your HP, and some also give you regeneration over several turns. Defense cards like shields give you BLOCK, which absorbs damage until it runs out. Use healing and defense wisely so you survive long enough to finish your enemies."
    },
    {
      title: "Hotseat Turns & Winning",
      content: "Only one hero acts at a time. The top of the screen and the banners say whose turn it is. That player can play cards, draw, or end their turn. When their turn ends, their hand goes to the discard pile, and the next hero's hand is drawn in. Reduce all other heroes to 0 HP to win the Trojan War!"
    }
  ];

  // Mobile small-screen warning: show before any game screen
  if (showMobileWarning) {
    return (
      <div className="min-h-screen text-amber-100 p-4 flex items-center justify-center" style={{backgroundColor: '#6b2f10'}}>
        <div className="bg-stone-900 p-6 rounded-lg border-2 border-amber-400 max-w-sm w-full mx-4 text-center">
          <h2 className="text-lg font-bold text-amber-300 mb-2">Small Screen Warning</h2>
          <p className="text-sm text-amber-100 mb-3">
            This game is not optimized for small screens. Some things may not display properly.
          </p>
          <p className="text-xs text-amber-300 mb-4">
            You can still continue and play at your own risk.
          </p>
          <button
            onClick={() => setShowMobileWarning(false)}
            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-bold"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // Credits page
  if (gameState === 'credits') {
    return renderCreditsPage();
  }

  // Tutorial Modal
  if (showTutorial) {
    return (
      <div className="min-h-screen text-amber-100 p-8 flex flex-col" style={{backgroundColor: '#6b2f10'}}>
        <div className="flex-1 flex items-center justify-center">
          <div className="max-w-2xl w-full bg-stone-800 p-8 rounded-lg border-4 border-amber-600 relative">
            <button
              onClick={() => setShowTutorial(false)}
              className="absolute top-4 right-4 text-amber-400 hover:text-amber-300"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-8 h-8 text-amber-400" />
              <h2 className="text-3xl font-bold text-amber-400">
                {tutorialPages[tutorialPage].title}
              </h2>
            </div>
            
            <p className="text-lg text-amber-100 mb-8 whitespace-pre-line leading-relaxed">
              {tutorialPages[tutorialPage].content}
            </p>
            
            <div className="flex items-center justify-between">
              <button
                onClick={() => setTutorialPage(Math.max(0, tutorialPage - 1))}
                disabled={tutorialPage === 0}
                className={`px-6 py-2 rounded-lg font-bold ${
                  tutorialPage === 0
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-amber-700 hover:bg-stone-700 text-white'
                }`}
              >
                Previous
              </button>
              
              <span className="text-amber-400">
                {tutorialPage + 1} / {tutorialPages.length}
              </span>
              
              {tutorialPage < tutorialPages.length - 1 ? (
                <button
                  onClick={() => setTutorialPage(tutorialPage + 1)}
                  className="px-6 py-2 rounded-lg font-bold bg-amber-700 hover:bg-stone-700 text-white"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowTutorial(false);
                    setTutorialPage(0);
                  }}
                  className="px-6 py-2 rounded-lg font-bold bg-green-700 hover:bg-green-600 text-white"
                >
                  Start Playing!
                </button>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (gameState === 'heroSelect') {
    return (
      <div className="min-h-screen text-amber-100 p-8" style={{backgroundColor: '#6b2f10'}}>
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold text-center mb-2 text-amber-400" style={{fontFamily: 'serif'}}>
            THE TROJAN WAR
          </h1>
          <p className="text-center text-amber-300 mb-8">Choose your heroes (4 players required)</p>
          
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => setShowTutorial(true)}
              className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-bold"
            >
              <BookOpen className="w-5 h-5" />
              How to Play
            </button>
            <button
              onClick={() => {
                setGameState('practice');
                setCurrentTrivia(getRandomTrivia(triviaQuestions));
                setPracticeStats({ correct: 0, total: 0 });
              }}
              className="flex items-center gap-2 bg-green-700 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-bold"
            >
              <BookOpen className="w-5 h-5" />
              Practice Questions
            </button>
            <button
              onClick={() => setGameState('credits')}
              className="flex items-center gap-2 bg-purple-700 hover:bg-purple-600 text-white px-6 py-3 rounded-lg font-bold"
            >
              <Crown className="w-5 h-5" />
              Credits
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Object.entries(HEROES).map(([id, hero]) => (
              <button
                key={id}
                onClick={() => selectHero(id)}
                className={`p-6 rounded-lg border-2 transition-all ${
                  players.find(p => p.heroId === id)
                    ? 'border-green-500 bg-green-900/30 opacity-60'
                    : 'border-stone-600 bg-stone-800 hover:bg-stone-700 hover:border-stone-500'
                }`}
              >
                <div className="text-center">
                  <img src={HERO_IMAGES[id]} alt={hero.name} draggable={false} className="w-24 h-24 object-cover rounded mx-auto mb-4 pixelated-art" onError={(e) => { e.currentTarget.src = HERO_IMAGES[id].replace('.svg', '.png'); }} />
                  <h3 className="text-xl font-bold text-amber-400 mb-2">{hero.name}</h3>
                  <p className="text-xs text-amber-300 mb-2">{hero.faction}</p>
                  <p className="text-xs text-amber-200">{HERO_DESCRIPTIONS[id]}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="bg-stone-800 p-6 rounded-lg mb-8">
            <h3 className="text-xl font-bold text-amber-400 mb-4">Selected Heroes ({players.length}/4)</h3>
            <div>
              {players.map((p, i) => (
                <div key={`${p.heroId}-${i}`}>
                  {/* Drop zone before each item - insert before this position */}
                  <div
                    onDragOver={handleDragOver}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDrop(i);
                    }}
                    className="h-2 -mb-1 rounded"
                  />
                  <div
                    draggable
                    onDragStart={(e) => {
                      // Only prevent if clicking directly on button element
                      if (e.target.tagName === 'BUTTON') {
                        e.preventDefault();
                        return;
                      }
                      handleDragStart(i);
                      if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', i.toString());
                      }
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // When dropping on the item itself, insert before it (at i)
                      // This allows dropping from any position to any position
                      handleDrop(i);
                    }}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center justify-between bg-stone-700 p-3 rounded cursor-move transition-all select-none ${
                      draggedPlayerIndex === i ? 'opacity-50' : 'hover:bg-stone-600'
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <GripVertical className="w-5 h-5 text-amber-400 cursor-grab" />
                      <span className="text-amber-200">Player {i + 1}: {HEROES[p.heroId].name}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        removePlayer(i);
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      draggable={false}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {/* Drop zone after the last item - allows dropping at the very end */}
              {players.length > 0 && (
                <div
                  onDragOver={handleDragOver}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(players.length);
                  }}
                  className="h-2 rounded"
                />
              )}
            </div>
          </div>

          <button
            onClick={startGame}
            disabled={players.length !== 4}
            className={`w-full py-4 rounded-lg text-xl font-bold ${
              players.length === 4
                ? 'bg-amber-600 hover:bg-stone-700 text-white'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {players.length === 4 ? 'BEGIN THE WAR' : `Need ${4 - players.length} more player(s)`}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (gameState === 'gameOver') {
    const winner = players.find(p => p.alive);
    return (
      <div
        className="min-h-screen text-amber-100 flex flex-col"
        style={{backgroundColor: '#6b2f10'}}
      >
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Crown className="w-32 h-32 text-yellow-400 mx-auto mb-4" />
            <h1
              className="text-6xl font-bold text-amber-400 mb-4"
              style={{fontFamily: 'serif'}}
            >
              VICTORY!
            </h1>
            <p className="text-3xl text-amber-300 mb-8">
              {winner?.hero} has won the Trojan War!
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-amber-600 hover:bg-stone-700 text-white px-8 py-4 rounded-lg text-xl font-bold"
            >
              Play Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Practice Mode
  if (gameState === 'practice') {
    return (
      <div className="min-h-screen text-amber-100 p-8 flex flex-col" style={{backgroundColor: '#6b2f10'}}>
        <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col items-center justify-center py-8">
          {/* Stats */}
          <div className="bg-stone-800 p-4 rounded-lg border-2 border-amber-600 mb-6 text-center w-full">
            <h2 className="text-3xl font-bold text-amber-400 mb-2">Practice Mode</h2>
            <div className="flex justify-center gap-8 mt-4">
              <div>
                <p className="text-amber-300 text-sm">Correct</p>
                <p className="text-3xl font-bold text-green-400">{practiceStats.correct}</p>
              </div>
              <div>
                <p className="text-amber-300 text-sm">Total</p>
                <p className="text-3xl font-bold text-blue-400">{practiceStats.total}</p>
              </div>
              {practiceStats.total > 0 && (
                <div>
                  <p className="text-amber-300 text-sm">Accuracy</p>
                  <p className="text-3xl font-bold text-purple-400">
                    {Math.round((practiceStats.correct / practiceStats.total) * 100)}%
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Question Screen */}
          {!triviaResult && currentTrivia && (
            <div className="bg-stone-800 p-8 rounded-lg border-4 border-amber-600 shadow-2xl w-full">
              <h2 className="text-3xl font-bold text-amber-400 mb-6 text-center">
                ⚡ TRIVIA QUESTION ⚡
              </h2>
              <p className="text-xl text-amber-100 mb-8 text-center leading-relaxed">
                {currentTrivia.question}
              </p>
              <div className="space-y-3">
                {currentTrivia.options?.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const isCorrect = option === currentTrivia.answer;
                      setTriviaResult({
                        correct: isCorrect,
                        selected: option
                      });
                      setPracticeStats({
                        correct: practiceStats.correct + (isCorrect ? 1 : 0),
                        total: practiceStats.total + 1
                      });
                    }}
                    className="w-full p-4 bg-stone-700 hover:bg-stone-700 rounded-lg text-left text-lg transition-all border-2 border-stone-700 hover:border-stone-500 hover:scale-105"
                  >
                    <span className="font-bold">{String.fromCharCode(65 + idx)}.</span> {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Result Screen */}
          {triviaResult && currentTrivia && (
              <div className={`p-8 rounded-lg border-4 shadow-2xl w-full ${
                triviaResult.correct 
                  ? 'bg-green-900 border-green-500 text-white' 
                  : 'bg-red-900 border-red-500 text-white'
              }`}>
              <div className="text-center">
                <div className="text-8xl mb-4">
                  {triviaResult.correct ? '✓' : '✗'}
                </div>
                <h2 className="text-4xl font-bold mb-4">
                  {triviaResult.correct ? 'CORRECT!' : 'INCORRECT!'}
                </h2>
                
                <div className="bg-black/40 p-4 rounded-lg mb-6">
                  <p className="text-lg text-amber-200 mb-2">
                    <strong>Question:</strong>
                  </p>
                  <p className="text-base text-amber-300 mb-4">
                    {currentTrivia.question}
                  </p>
                  <p className="text-lg text-amber-200 mb-2">
                    <strong>Correct Answer:</strong>
                  </p>
                  <p className={`text-base ${triviaResult.correct ? 'text-green-300' : 'text-green-300'}`}>
                    {currentTrivia.answer}
                  </p>
                  {!triviaResult.correct && (
                    <>
                      <p className="text-lg text-amber-200 mt-4 mb-2">
                        <strong>Your Answer:</strong>
                      </p>
                      <p className="text-base text-red-300">
                        {triviaResult.selected}
                      </p>
                    </>
                  )}
                </div>

                <button
                  onClick={() => {
                    setCurrentTrivia(getRandomTrivia(triviaQuestions));
                    setTriviaResult(null);
                  }}
                  className={`px-8 py-4 rounded-lg text-xl font-bold transition-all hover:scale-105 ${
                    triviaResult.correct 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  } text-white`}
                  >
                  Next Question
                </button>
                
                <button
                  onClick={() => {
                    setGameState('heroSelect');
                    setCurrentTrivia(null);
                    setTriviaResult(null);
                  }}
                  className="px-8 py-4 rounded-lg text-xl font-bold bg-amber-700 hover:bg-stone-700 text-white transition-all hover:scale-105"
                >
                  Exit to Menu
                </button>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  const currentPlayer = players[currentPlayerIndex];

  return (
    <div className="min-h-screen text-amber-100 p-4" style={{backgroundColor: '#6b2f10'}}>

      {/* Trivia Modal for main game */}
      {triviaQuestion && !triviaResult && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-800 p-8 rounded-lg border-4 border-amber-600 shadow-2xl max-w-2xl w-full">
            <h2 className="text-3xl font-bold text-amber-400 mb-6 text-center">
              ⚡ TRIVIA QUESTION ⚡
            </h2>
            <p className="text-xl text-amber-100 mb-8 text-center leading-relaxed">
              {triviaQuestion.question}
            </p>
            <div className="space-y-3">
              {triviaQuestion.options?.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => answerTrivia(option)}
                  className="w-full p-4 bg-stone-700 hover:bg-stone-600 rounded-lg text-left text-lg transition-all border-2 border-stone-700 hover:border-stone-500 hover:scale-105"
                >
                  <span className="font-bold">{String.fromCharCode(65 + idx)}.</span> {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trivia Result Modal */}
      {triviaResult && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className={`p-8 rounded-lg border-4 shadow-2xl max-w-2xl w-full ${
            triviaResult.correct 
              ? 'bg-green-900 border-green-500 text-white' 
              : 'bg-red-900 border-red-500 text-white'
          }`}>
            <div className="text-center">
              <div className="text-8xl mb-4">
                {triviaResult.correct ? '✓' : '✗'}
              </div>
              <h2 className="text-4xl font-bold mb-4">
                {triviaResult.correct ? 'CORRECT!' : 'INCORRECT!'}
              </h2>
              <button
                onClick={continueAfterTrivia}
                className={`px-8 py-4 rounded-lg text-xl font-bold transition-all hover:scale-105 ${
                  triviaResult.correct 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                } text-white`}
                >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-800 p-8 rounded-lg border-4 border-amber-600 shadow-2xl max-w-md w-full">
            <h2 className="text-3xl font-bold text-amber-400 mb-6 text-center">
              Exit Game?
            </h2>
            <p className="text-lg text-amber-200 mb-8 text-center">
              Are you sure you want to exit? Your current game progress will be lost.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="px-6 py-3 rounded-lg font-bold bg-stone-700 hover:bg-stone-600 text-white transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setGameState('heroSelect');
                  setPlayers([]);
                  setCurrentPlayerIndex(0);
                  setGameLog([]);
                  setTriviaQuestion(null);
                  setTriviaResult(null);
                  setTargetSelection(null);
                  setPendingCardExecution(null);
                  setHoveredCard(null);
                  setRoundCounter(0);
                  setIsDiscarding(false);
                  setDrawPile([]);
                  setDiscardPile([]);
                  setShowExitConfirm(false);
                }}
                className="px-6 py-3 rounded-lg font-bold bg-red-700 hover:bg-red-600 text-white transition-all"
              >
                Exit Game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="max-w-7xl mx-auto mb-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowExitConfirm(true)}
            className="text-2xl font-bold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer"
            style={{fontFamily: 'serif'}}
          >
            THE TROJAN WAR
          </button>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-blue-900/50 px-4 py-2 rounded-lg border-2 border-blue-700">
              <Zap className="w-5 h-5 text-blue-400" />
              <span className="text-xl font-bold">{currentPlayer.energy}/{currentPlayer.maxEnergy}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Instructions - shown only on mobile */}
      <div className="lg:hidden max-w-7xl mx-auto mb-4">
        {isDiscarding ? (
          <div className="bg-red-900/80 p-4 rounded-lg border-2 border-red-500 text-center">
            <p className="text-red-200 font-bold text-lg mb-1">DISCARD A CARD</p>
            <p className="text-red-100 text-sm leading-relaxed">
              Choose a card from your hand to discard, and you will draw a new one.
            </p>
          </div>
        ) : (
          <div className="bg-stone-800/80 p-4 rounded-lg border-2 border-amber-400 text-center">
            <p className="text-amber-300 font-bold text-base mb-1">⚔️ {currentPlayer.hero}'s Turn</p>
            <p className="text-amber-100 text-xs leading-relaxed">
              {targetSelection === 'selectTarget'
                ? '👈 Select an opponent to attack'
                : 'Click a card to play it or End Turn to pass'}
            </p>
          </div>
        )}
      </div>

      {/* Board: current hero and opponents */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch py-6">
        {/* Left: Current Player (visual) */}
        <div className="h-full flex flex-col items-center">
          <div className="bg-stone-900 p-6 pr-10 lg:pr-24 rounded-lg border-4 border-amber-400 w-full shadow-lg h-full mb-0 flex items-center justify-center">
            {/* Left: larger hero image */}
            <div className="flex-shrink-0 flex justify-center items-center mr-10">
              <img
                src={HERO_IMAGES[currentPlayer.heroId]}
                alt={currentPlayer.hero}
                draggable={false}
                className="w-52 h-52 lg:w-60 lg:h-60 object-cover rounded pixelated-art"
                onError={(e) => {
                  e.currentTarget.src = HERO_IMAGES[currentPlayer.heroId].replace('.svg', '.png');
                }}
              />
            </div>

            {/* Right: hero text, stats, and effects */}
            <div className="flex flex-col justify-center max-w-md">
              <h2 className="text-3xl font-bold text-amber-100 mb-1">{currentPlayer.hero}</h2>
              <p className="text-sm text-amber-300 italic mb-3 max-w-md">{HERO_DESCRIPTIONS[currentPlayer.heroId]}</p>

              <div className="flex items-center gap-6 mb-3">
                <div className="flex items-center gap-2">
                  <Heart className="w-6 h-6 text-red-500" />
                  <span className="text-lg font-bold">{currentPlayer.hp}/{currentPlayer.maxHp}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-400" />
                  <span className={`text-lg font-bold ${currentPlayer.block > 0 ? 'text-blue-400' : 'text-stone-500'}`}>{currentPlayer.block}</span>
                  {currentPlayer.effects.find(e => e.type === 'damageReduction') && (
                    <div title={`${currentPlayer.effects.find(e => e.type === 'damageReduction').count} use(s) left`}>
                      <Shield className="w-5 h-5 text-purple-400" />
                    </div>
                  )}
                </div>
              </div>

              {renderEffects(currentPlayer.effects)}
            </div>

          </div>
        </div>

        {/* Right: Opponents (responsive grid) with desktop instructions above */}
        <div className="lg:col-span-1 h-full flex flex-col">
          {/* Desktop Instructions - shown only on desktop above enemies */}
          <div className="hidden lg:block mb-4">
            {isDiscarding ? (
              <div className="bg-red-900/80 p-4 rounded-lg border-2 border-red-500 text-center">
                <p className="text-red-200 font-bold text-lg mb-1">DISCARD A CARD</p>
                <p className="text-red-100 text-sm leading-relaxed">
                  Choose a card from your hand to discard, and you will draw a new one.
                </p>
              </div>
            ) : (
              <div className="bg-stone-800/80 p-4 rounded-lg border-2 border-amber-400 text-center">
                <p className="text-amber-300 font-bold text-base mb-1">⚔️ {currentPlayer.hero}'s Turn</p>
                <p className="text-amber-100 text-xs leading-relaxed">
                  {targetSelection === 'selectTarget'
                    ? '👈 Select an opponent to attack'
                    : 'Click a card to play it or End Turn to pass'}
                </p>
              </div>
            )}
          </div>

          <div className="w-full">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid lg:grid-cols-3 gap-4">
              {opponentsOrdered.map(({ player, idx }) => (
                <div
                  key={idx}
                  className={`w-full p-5 rounded-lg border-2 transition-all h-full flex flex-col justify-between ${
                    player.alive
                      ? targetSelection === 'selectTarget'
                        ? 'border-red-500 bg-red-900/10 cursor-pointer hover:bg-red-900/20'
                        : 'border-amber-400 bg-stone-800'
                      : 'border-gray-700 bg-gray-900 opacity-50'
                  }`}
                  onClick={() => {
                    if (targetSelection === 'selectTarget' && player.alive) selectTarget(idx);
                  }}
                >
                  <img
                    src={HERO_IMAGES[player.heroId]}
                    alt={player.hero}
                    draggable={false}
                    className="w-24 h-24 lg:w-36 lg:h-36 object-cover rounded mb-3 mx-auto pixelated-art"
                    onError={(e) => {
                      e.currentTarget.src = HERO_IMAGES[player.heroId].replace('.svg', '.png');
                    }}
                  />
                  <div className="font-bold text-xs lg:text-sm mb-1 text-center">{player.hero}</div>
                  <div className="flex items-center gap-2 mb-1 justify-center">
                    <div className="w-24 lg:w-40 bg-stone-900 rounded-full h-2 lg:h-3">
                      <div
                        className="bg-red-600 h-2 lg:h-3 rounded-full transition-all"
                        style={{ width: `${(player.hp / player.maxHp) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold">{player.hp}/{player.maxHp}</span>
                  </div>
                  <div className="flex items-center gap-1 justify-center">
                    <Shield className="w-3 h-3 lg:w-3 lg:h-3 text-blue-400" />
                    <span
                      className={`text-[0.65rem] lg:text-xs font-bold ${
                        player.block > 0 ? 'text-blue-400' : 'text-stone-500'
                      }`}
                    >
                      {player.block} Block
                    </span>
                  </div>
                  {renderEffects(player.effects)}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hand: placed under the current player and opponents */}
      <div className="max-w-7xl mx-auto mt-4 pb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold text-amber-400">Your Hand <span className="text-amber-200 text-sm font-normal">({currentPlayer.hand.length}/6 cards)</span></h3>
          <div className="flex gap-2">
            <button
              onClick={endTurn}
              disabled={isDiscarding}
              className="bg-red-700 hover:bg-red-600 px-8 py-3 rounded-lg font-bold text-lg border-2 border-red-900 hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              End Turn
            </button>
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 px-4 pt-8 pb-4 card-scrollbar">
          <div className="flex gap-3 pb-2">
          {currentPlayer.hand.map((cardId, idx) => {
            const card = CARDS[cardId];
            const canPlay = currentPlayer.energy >= card.cost && !targetSelection && !isDiscarding;
            const cardKey = `${idx}-${cardId}`;
            const isFlipping = flippingCards.includes(`${currentPlayerIndex}-${idx}`);

            return (
              <button
                key={cardKey}
                onClick={() => {
                  if (isDiscarding) {
                    handleDiscard(cardId);
                  } else if (canPlay) {
                    playCard(cardId);
                  }
                }}
                onMouseEnter={() => (canPlay || isDiscarding) && setHoveredCard(cardKey)}
                onMouseLeave={() => setHoveredCard(null)}
                disabled={!canPlay && !isDiscarding}
                className={`relative overflow-hidden inline-block align-top flex-shrink-0 w-72 h-64 p-4 rounded-lg border-2 transition-all ${
                  isDiscarding 
                    ? 'bg-stone-800 border-red-500 hover:border-red-400 cursor-pointer shadow-lg' 
                    : canPlay 
                    ? `bg-stone-800 ${isFlipping ? 'border-amber-500' : getRarityColor(card.rarity)} cursor-pointer shadow-lg`
                    : `bg-stone-900 border-gray-700 cursor-not-allowed`
                } ${hoveredCard === cardKey && (canPlay || isDiscarding) ? (isDiscarding ? 'ring-4 ring-red-400/25 -translate-y-6 shadow-2xl' : (!isFlipping ? getRarityHover(card.rarity) : '') + ' ring-4 -translate-y-6 shadow-2xl') : 'hover:shadow-lg'}`}
              >
                {/* Flip container */}
                <div
                  className="w-full h-full"
                  style={{
                    perspective: '1000px',
                  }}
                >
                  <div
                    className="w-full h-full relative transition-transform duration-500"
                    style={{
                      transformStyle: 'preserve-3d',
                      transform: isFlipping ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    }}
                  >
                    {/* Card front */}
                    <div
                      className="absolute inset-0 flex flex-col"
                      style={{ backfaceVisibility: 'hidden' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(card.type)}
                          <span className="text-xs font-bold text-amber-100 uppercase">{card.type}</span>
                        </div>
                        <div className="bg-blue-900 text-blue-100 rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg border-2 border-blue-700">
                          {card.cost}
                        </div>
                      </div>

                      <h4 className="text-lg font-bold text-amber-100 mb-3 break-words">{card.name}</h4>

                      <div className="bg-black/40 p-3 rounded mb-2 text-left space-y-2">
                        <p className="text-xs text-amber-200 break-words">
                          <strong className="text-amber-100">Normal:</strong> {card.normal}
                        </p>
                        <p className="text-xs text-green-300 break-words">
                          <strong className="text-green-100">⚡ Bonus:</strong> {card.bonus}
                        </p>
                      </div>

                      <p className="text-xs text-amber-300 italic text-left break-words">
                        "{card.lore}"
                      </p>
                    </div>

                    {/* Card back */}
                    <div
                      className="absolute inset-0 flex items-center justify-center rounded-lg border-2 border-amber-500 bg-stone-900 text-sm font-bold text-amber-300"
                      style={{
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                      }}
                    >
                      THE TROJAN WAR
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
            </div>
        </div>

        {/* Deck Info & Actions */}
        <div className="flex gap-8 text-sm mt-3 items-center">
          {/* Draw Pile display */}
          <div className="flex flex-col items-center">
              <div className="relative w-24 h-36">
                  <div className="absolute top-0 left-0 w-full h-full rounded-lg bg-stone-700 border-2 border-amber-600 transform -rotate-6"></div>
                  <div className="absolute top-0 left-0 w-full h-full rounded-lg bg-stone-700 border-2 border-amber-600 transform rotate-6"></div>
                  <div className="relative w-full h-full rounded-lg bg-stone-800 border-2 border-amber-600 flex flex-col items-center justify-center overflow-hidden">
                      <span className="text-amber-400 text-sm">Draw Pile</span>
                      <span className="font-bold text-2xl text-white">{drawPile.length}</span>
                  </div>
              </div>
              <button
                onClick={handleDrawAndDiscard}
                disabled={isDiscarding || currentPlayer.energy < 1 || drawPile.length === 0}
                className="mt-2 bg-blue-700 hover:bg-blue-600 px-3 py-1 rounded-lg font-bold text-sm border-2 border-blue-900 hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Draw (1⚡)
              </button>
          </div>



          {/* Discard Pile display */}
          <div className="flex flex-col items-center">
              <div className="relative w-24 h-36">
                  <div className="absolute top-0 left-0 w-full h-full rounded-lg bg-stone-700 border-2 border-stone-600 transform -rotate-3"></div>
                  <div className="absolute top-0 left-0 w-full h-full rounded-lg bg-stone-700 border-2 border-stone-600 transform rotate-3"></div>
                  <div className="relative w-full h-full rounded-lg bg-stone-800 border-2 border-stone-600 flex flex-col items-center justify-center">
                      <span className="text-stone-400 text-sm">Discard Pile</span>
                      <span className="font-bold text-2xl text-white">{discardPile.length}</span>
                  </div>
              </div>
              <button
                onClick={handleShuffleDiscard}
                disabled={isDiscarding || currentPlayer.energy < 2 || discardPile.length === 0}
                className="mt-2 bg-purple-700 hover:bg-purple-600 px-3 py-1 rounded-lg font-bold text-sm border-2 border-purple-900 hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Shuffle Discard (2⚡)
              </button>
          </div>
        </div>
      </div>

      {/* Battle Log: bottom of page */}
      <div className="max-w-7xl mx-auto mt-6 mb-8">
        <div className="bg-stone-800 p-4 rounded-lg border-2 border-amber-700">
          <h3 className="text-sm font-bold text-amber-400 mb-2">Battle Log</h3>
          <div className="space-y-1 text-xs max-h-48 overflow-y-auto">
            {gameLog.map(log => (
              <div
                key={log.id}
                className={`${
                  log.type === 'turn' ? 'text-amber-300 font-bold' :
                  log.type === 'damage' ? 'text-red-400' :
                  log.type === 'success' ? 'text-green-400 font-bold' :
                  log.type === 'death' ? 'text-red-600 font-bold' :
                  log.type === 'victory' ? 'text-yellow-400 font-bold' :
                  log.type === 'card' ? 'text-purple-300' :
                  log.type === 'effect' ? 'text-blue-300' :
                  'text-amber-200'
                }`}
              >
                {log.message}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TrojanWarGame;